import { useState, useEffect } from "react";
import instance, { generateNonce } from "../../service/apiService";
import { getStore } from "../../service/storeRef";

const ELECTRON = "http://127.0.0.1:59871";

// Module-level cache — fetched once for the entire app session
let _cachedLatestVersion: string | null = null;
let _versionFetchPromise: Promise<void> | null = null;

function fetchLatestVersionOnce(): Promise<void> {
  if (_cachedLatestVersion !== null) return Promise.resolve();
  if (_versionFetchPromise) return _versionFetchPromise;
  _versionFetchPromise = instance
    .get("/document/filepicker/latest.yml", { responseType: "text" })
    .then((res) => {
      const match = String(res.data).match(/^version:\s*(.+)$/m);
      if (match) _cachedLatestVersion = match[1].trim();
    })
    .catch(() => { });
  return _versionFetchPromise;
}

function detectPlatform() {
  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';

  if (ua.includes('win') || platform.includes('win')) {
    if (ua.includes('arm') || platform.includes('arm')) return 'win_arm';
    return 'win64';
  }
  if (ua.includes('mac') || platform.includes('mac')) {
    if (platform.includes('arm') || ua.includes('arm64')) return 'darwin_arm';
    return 'darwin';
  }
  if (ua.includes('linux') || platform.includes('linux')) return 'linux';
  return 'win64';
}

const INSTALLER_NAMES = {
  win32: 'AssystLinkApp-Setup-x64.exe',
  win64: 'AssystLinkApp-Setup-x64.exe',
  win_arm: 'AssystLinkApp-Setup-arm64.exe',
  darwin: 'AssystLinkApp.dmg',
  darwin_arm: 'AssystLinkApp-arm64.dmg',
  linux: 'AssystLinkApp.AppImage',
};


interface PickOptions {
  mode?: "file" | "folder" | "both";
  onNotInstalled?: () => void;
  onUpdateAvailable?: () => void;
}

export function useAssystFilePicker() {
  const [isLoading, setIsLoading] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(_cachedLatestVersion);

  const platform = detectPlatform();
  const installerName = INSTALLER_NAMES[platform];
  const downloadUrl = `/document/filepicker/download?platform=${platform}`;

  useEffect(() => {
    if (_cachedLatestVersion !== null) return;
    fetchLatestVersionOnce().then(() => {
      if (_cachedLatestVersion) setLatestVersion(_cachedLatestVersion);
    });
  }, []);

  const pickFile = async (opts: PickOptions = {}): Promise<string | null> => {
    const { mode = "file", onNotInstalled, onUpdateAvailable } = opts;
    setIsLoading(true);
    setNeedsUpdate(false);

    try {
      // 1. Health check — detect if Electron app is running
      let health: { status: string; deviceId: string; version: string };
      try {
        const res = await fetch(`${ELECTRON}/health`);
        health = await res.json();
        if (health.status !== "ok") throw new Error();
      } catch {
        onNotInstalled?.();
        return null;
      }

      // 2. Version check — compare running version against latest from backend
      let versionMismatch = false;
      if (latestVersion && health.version && health.version !== latestVersion) {
        versionMismatch = true;
        setNeedsUpdate(true);
      }

      // 3. Get challenge token (auth/nonce/timestamp handled by axios interceptor)
      const tokenRes = await instance.get(
        `/document/filepicker/token?deviceId=${encodeURIComponent(health.deviceId)}`
      );
      const pickerToken: string = tokenRes.data?.token;
      if (!pickerToken) return null;

      // 4. Open the file/folder picker in the Electron app
      const UserToken = getStore()?.getState().authUser.userToken;
      const userId = getStore()?.getState().authUser.userId;
      const refreshToken = getStore()?.getState().authUser.refreshToken;
      const nonce = generateNonce();
      const pickRes = await fetch(`${ELECTRON}/pick`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${UserToken.trim()}`,
          userid: userId,
          refreshtoken: refreshToken,
          "x-Nonce": nonce,
          "x-Timestamp": new Date().toISOString(),
        },
        body: JSON.stringify({
          token: pickerToken,
          deviceId: health.deviceId,
          mode,
        }),
      });
      const pickData = await pickRes?.json();

      if (!pickData.success || pickData.canceled) return null;

      const paths: string[] = pickData.paths ?? (pickData.path ? [pickData.path] : []);
      if (paths.length === 0) return null;

      // 5. Log the pick (fire-and-forget, auth handled by interceptor)
      instance
        .post("/document/filepicker/log-pick", { deviceId: health.deviceId, filePaths: paths })
        .catch(() => { });

      // 6. Notify about update after a successful pick (non-blocking)
      if (versionMismatch) onUpdateAvailable?.();

      return paths[0];
    } finally {
      setIsLoading(false);
    }
  };

  const openFile = async (filePath: string): Promise<boolean> => {
    try {
      const healthRes = await fetch(`${ELECTRON}/health`);
      const health = await healthRes.json();
      if (health.status !== "ok") return false;

      const tokenRes = await instance.get(
        `/document/filepicker/token?deviceId=${encodeURIComponent(health.deviceId)}`
      );
      const pickerToken: string = tokenRes.data?.token;
      if (!pickerToken) return false;

      const UserToken = getStore()?.getState().authUser.userToken;
      const refreshToken = getStore()?.getState().authUser.refreshToken;
      const nonce = generateNonce();
      const res = await fetch(`${ELECTRON}/open`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${UserToken.trim()}`,
          refreshtoken: refreshToken,
          "x-Nonce": nonce,
          "x-Timestamp": new Date().toISOString(),
        },
        body: JSON.stringify({ token: pickerToken, deviceId: health.deviceId, filePath }),
      });
      const data = await res.json();
      return data.success === true;
    } catch {
      return false;
    }
  };

  return { pickFile, openFile, isLoading, needsUpdate, latestVersion, platform, installerName, downloadUrl };
}
